import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Video,
  Plus,
  Edit,
  Trash2,
  Users,
  Calendar,
  Clock,
  ArrowLeft,
  Loader2,
  Eye,
  Play,
  Youtube,
  X,
  Save
} from 'lucide-react';

const AdminWebinars = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const emptyForm = {
    title: '',
    description: '',
    instructor: '',
    date: '',
    duration_minutes: 90,
    platform: 'youtube',
    meeting_url: '',
    meeting_id: '',
    password: '',
    max_attendees: '',
    price: 0,
    category: 'general',
    level: 'basico',
    is_recorded: false,
    recording_url: ''
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    // Allow access in development mode
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    try {
      const response = await api.get('/webinars/admin/all');
      setWebinars(response.data);
    } catch (error) {
      console.error('Error fetching webinars:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los webinars",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        duration_minutes: parseInt(formData.duration_minutes),
        price: parseFloat(formData.price) || 0
      };

      if (editingWebinar) {
        await api.put(`/webinars/admin/${editingWebinar.id}`, payload);
        toast({ title: "Webinar actualizado" });
      } else {
        await api.post('/webinars/admin/create', payload);
        toast({ title: "Webinar creado" });
      }

      setShowForm(false);
      setEditingWebinar(null);
      setFormData(emptyForm);
      fetchWebinars();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo guardar",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (webinar) => {
    setEditingWebinar(webinar);
    setFormData({
      title: webinar.title || '',
      description: webinar.description || '',
      instructor: webinar.instructor || '',
      date: webinar.date ? webinar.date.slice(0, 16) : '',
      duration_minutes: webinar.duration_minutes || 90,
      platform: webinar.platform || 'youtube',
      meeting_url: webinar.meeting_url || '',
      meeting_id: webinar.meeting_id || '',
      password: webinar.password || '',
      max_attendees: webinar.max_attendees || '',
      price: webinar.price || 0,
      category: webinar.category || 'general',
      level: webinar.level || 'basico',
      is_recorded: webinar.is_recorded || false,
      recording_url: webinar.recording_url || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (webinarId) => {
    if (!confirm('¿Estás seguro de eliminar este webinar?')) return;

    try {
      await api.delete(`/webinars/admin/${webinarId}`);
      toast({ title: "Webinar eliminado" });
      fetchWebinars();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const categories = [
    { id: 'marketing', name: 'Marketing Digital' },
    { id: 'legal', name: 'Aspectos Legales' },
    { id: 'financiamiento', name: 'Financiamiento' },
    { id: 'ventas', name: 'Técnicas de Venta' },
    { id: 'certificacion', name: 'Certificación CONOCER' },
    { id: 'tecnologia', name: 'Tecnología' },
    { id: 'general', name: 'General' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Gestión de Webinars</h1>
                <p className="text-sm text-gray-500">{webinars.length} webinars</p>
              </div>
            </div>
            <Button 
              className="bg-gray-900 hover:bg-gray-800"
              onClick={() => {
                setEditingWebinar(null);
                setFormData(emptyForm);
                setShowForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Webinar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  {editingWebinar ? 'Editar Webinar' : 'Nuevo Webinar'}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Título *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Descripción</Label>
                      <textarea
                        className="w-full rounded-md border border-gray-300 p-2 text-sm"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label>Instructor *</Label>
                      <Input
                        value={formData.instructor}
                        onChange={(e) => setFormData({...formData, instructor: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Fecha y Hora *</Label>
                      <Input
                        type="datetime-local"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Duración (minutos)</Label>
                      <Input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label>Plataforma</Label>
                      <select
                        className="w-full rounded-md border border-gray-300 p-2"
                        value={formData.platform}
                        onChange={(e) => setFormData({...formData, platform: e.target.value})}
                      >
                        <option value="youtube">YouTube Live (ilimitado)</option>
                        <option value="zoom">Zoom</option>
                        <option value="meet">Google Meet</option>
                        <option value="vimeo">Vimeo</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label>Categoría</Label>
                      <select
                        className="w-full rounded-md border border-gray-300 p-2"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label>Nivel</Label>
                      <select
                        className="w-full rounded-md border border-gray-300 p-2"
                        value={formData.level}
                        onChange={(e) => setFormData({...formData, level: e.target.value})}
                      >
                        <option value="basico">Básico</option>
                        <option value="intermedio">Intermedio</option>
                        <option value="avanzado">Avanzado</option>
                      </select>
                    </div>
                    
                    <div className="col-span-2">
                      <Label>URL del Meeting/Stream</Label>
                      <Input
                        value={formData.meeting_url}
                        onChange={(e) => setFormData({...formData, meeting_url: e.target.value})}
                        placeholder="https://youtube.com/live/..."
                      />
                    </div>
                    
                    <div>
                      <Label>ID de Meeting</Label>
                      <Input
                        value={formData.meeting_id}
                        onChange={(e) => setFormData({...formData, meeting_id: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label>Contraseña</Label>
                      <Input
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label>Precio (MXN)</Label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label>Máx. Asistentes (vacío = ilimitado)</Label>
                      <Input
                        type="number"
                        value={formData.max_attendees}
                        onChange={(e) => setFormData({...formData, max_attendees: e.target.value})}
                        placeholder="Sin límite"
                      />
                    </div>
                    
                    <div className="col-span-2 flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_recorded}
                          onChange={(e) => setFormData({...formData, is_recorded: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Tiene grabación disponible</span>
                      </label>
                    </div>
                    
                    {formData.is_recorded && (
                      <div className="col-span-2">
                        <Label>URL de la Grabación</Label>
                        <Input
                          value={formData.recording_url}
                          onChange={(e) => setFormData({...formData, recording_url: e.target.value})}
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-gray-900" disabled={saving}>
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {editingWebinar ? 'Actualizar' : 'Crear'}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Webinars List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : webinars.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Video className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay webinars creados</p>
              <Button 
                className="mt-4 bg-gray-900"
                onClick={() => setShowForm(true)}
              >
                Crear primer webinar
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {webinars.map(webinar => (
              <Card key={webinar.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="h-12 w-12 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                        {webinar.platform === 'youtube' ? (
                          <Youtube className="h-6 w-6 text-white" />
                        ) : (
                          <Video className="h-6 w-6 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{webinar.title}</h3>
                          {!webinar.is_active && (
                            <Badge variant="outline" className="text-gray-500">Inactivo</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{webinar.instructor}</p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(webinar.date)}
                          </span>
                          <span className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {webinar.current_attendees || 0} registrados
                          </span>
                          <Badge className={
                            webinar.price === 0 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }>
                            {webinar.price === 0 ? 'Gratis' : `$${webinar.price}`}
                          </Badge>
                          <Badge variant="outline">{webinar.category}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(webinar)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(webinar.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWebinars;
